import React, { useMemo, useState, useEffect } from 'react';
import {
  getMonthDays, getMondayBasedIndex, toISODate,
  getStatusColor, getStatusLabel,
  parseShiftTime, shortLocationCode, groupShiftsByDate
} from '../../../utils/grafikHelpers';
import { grafikService } from '../../../services/grafikService';

const WEEKDAY_HEADERS = ['Pn', 'Wt', 'Sr', 'Cz', 'Pt', 'So', 'Nd'];

// Helpery dla typow dostepnosci (grafik_dostepnosc.typ)
const AVAILABILITY_EMOJI = {
  dostepny: '\u{1F7E2}',      // green circle
  niedostepny: '\u{1F534}',   // red circle
  preferowany: '\u{2B50}',    // star
};
const AVAILABILITY_LABEL = {
  dostepny: 'Dostepny',
  niedostepny: 'Niedostepny',
  preferowany: 'Preferowany',
};
const AVAILABILITY_COLOR = {
  dostepny: '#28a745',
  niedostepny: '#dc3545',
  preferowany: '#ffc107',
};

/**
 * Siatka kalendarza miesiaca z kafelkami zmian.
 * Props:
 *  year, month (1-12)
 *  shifts: lista zmian (caly miesiac dla 1 pracownika lub wszyscy)
 *  coworkerShifts: zmiany wszystkich pracownikow (do pokazania kto jeszcze pracuje danego dnia)
 *  selectedUserLogin: jesli ustawione - filtruj po userze (widok per-user)
 *  onShiftClick(shift)
 *  onEmptyDayClick(date)  - opcjonalny, zwraca Date
 *  highlightToday
 *  readOnly: ukrywa hover/akcje add
 *  renderCellExtra(date, shifts) - dodatkowy render w komorce
 */
const MonthCalendar = ({
  year, month, shifts = [],
  coworkerShifts = [],
  selectedUserLogin = null,
  onShiftClick,
  onEmptyDayClick,
  highlightToday = true,
  readOnly = false,
  renderCellExtra
}) => {
  const days = useMemo(() => getMonthDays(year, month), [year, month]);

  const filteredShifts = useMemo(() => {
    if (!selectedUserLogin) return shifts;
    return shifts.filter(s => (s.user_login || s.login) === selectedUserLogin);
  }, [shifts, selectedUserLogin]);

  const byDate = useMemo(() => groupShiftsByDate(filteredShifts), [filteredShifts]);

  // Zmiany INNYCH pracownikow per dzien (do pokazania kto jeszcze pracuje).
  // Aktywne tylko gdy wybrany pracownik (widok pojedynczy).
  const coworkersByDate = useMemo(() => {
    if (!selectedUserLogin || !Array.isArray(coworkerShifts) || coworkerShifts.length === 0) {
      return {};
    }
    const others = coworkerShifts.filter(s => (s.user_login || s.login) !== selectedUserLogin);
    return groupShiftsByDate(others);
  }, [coworkerShifts, selectedUserLogin]);

  // Dostepnosc pracownika (gdy wybrany pracownik) - mapa data -> [entries]
  const [availability, setAvailability] = useState({});
  const [hoveredIso, setHoveredIso] = useState(null);

  useEffect(() => {
    if (!selectedUserLogin || !year || !month) {
      setAvailability({});
      return;
    }
    let cancelled = false;
    grafikService.getUserAvailability(selectedUserLogin, year, month)
      .then(res => {
        if (cancelled) return;
        const entries = res?.data?.entries || res?.entries || [];
        const byDateMap = {};
        (Array.isArray(entries) ? entries : []).forEach(e => {
          if (!e || !e.data) return;
          const key = String(e.data).slice(0, 10);
          if (!byDateMap[key]) byDateMap[key] = [];
          byDateMap[key].push(e);
        });
        setAvailability(byDateMap);
      })
      .catch(err => {
        console.warn('[MonthCalendar] availability fetch failed:', err?.message || err);
        if (!cancelled) setAvailability({});
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserLogin, year, month]);

  const todayIso = toISODate(new Date());
  const firstDayOffset = days.length > 0 ? getMondayBasedIndex(days[0]) : 0;
  const totalCells = firstDayOffset + days.length;
  const rows = Math.ceil(totalCells / 7);

  const cellBg = 'var(--card-bg)';
  const emptyCellBg = 'var(--bg)';

  return (
    <div style={{ width: '100%' }}>
      {/* Naglowek dni tygodnia */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '2px',
        marginBottom: '2px'
      }}>
        {WEEKDAY_HEADERS.map(d => (
          <div key={d} style={{
            padding: '0.4rem',
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            backgroundColor: 'var(--bg)',
            borderRadius: '4px'
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Siatka */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridTemplateRows: `repeat(${rows}, minmax(80px, 1fr))`,
        gap: '2px'
      }}>
        {/* Pierwsze puste komorki */}
        {Array.from({ length: firstDayOffset }).map((_, idx) => (
          <div key={`empty-${idx}`} style={{
            backgroundColor: emptyCellBg,
            borderRadius: '4px',
            opacity: 0.3
          }} />
        ))}

        {days.map(date => {
          const iso = toISODate(date);
          const dayShifts = byDate[iso] || [];
          const dayCoworkers = coworkersByDate[iso] || [];
          const isToday = iso === todayIso;
          const dow = getMondayBasedIndex(date);
          const isWeekend = dow >= 5;

          const dayAvailability = availability[iso] || [];
          const hasAvailability = dayAvailability.length > 0;
          // Kolor - bierzemy pierwszy wpis (najwczesniejszy w dniu)
          const availColor = hasAvailability
            ? (AVAILABILITY_COLOR[dayAvailability[0].typ] || 'var(--text-muted)')
            : null;
          const availEmoji = hasAvailability
            ? (AVAILABILITY_EMOJI[dayAvailability[0].typ] || '')
            : '';
          const availLabel = hasAvailability
            ? (AVAILABILITY_LABEL[dayAvailability[0].typ] || '')
            : '';
          const tooltipVisible = hoveredIso === iso && hasAvailability;

          return (
            <div
              key={iso}
              style={{
                // Cale tlo komorki w kolorze dostepnosci (przezroczyste 22) - dobrze widoczne
                backgroundColor: hasAvailability
                  ? `${availColor}22`
                  : (isWeekend ? 'var(--bg)' : cellBg),
                border: isToday && highlightToday
                  ? '2px solid var(--info)'
                  : (hasAvailability ? `2px solid ${availColor}` : '1px solid var(--border)'),
                borderRadius: '4px',
                padding: '0.4rem',
                minHeight: '80px',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.25rem',
                position: 'relative',
                cursor: !readOnly && onEmptyDayClick ? 'pointer' : 'default'
              }}
              onMouseEnter={() => hasAvailability && setHoveredIso(iso)}
              onMouseLeave={() => setHoveredIso(null)}
              onClick={(e) => {
                if (!readOnly && onEmptyDayClick && e.target === e.currentTarget) {
                  onEmptyDayClick(date);
                }
              }}
            >
              {/* Pasek dostepnosci - widoczny u gory komorki */}
              {hasAvailability && (
                <div
                  title={`${availLabel} - ${dayAvailability.length} wpis(ow)`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: availColor,
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    padding: '2px 6px',
                    borderTopLeftRadius: '3px',
                    borderTopRightRadius: '3px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    pointerEvents: 'none',
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <span style={{ fontSize: '0.85rem' }}>{availEmoji}</span>
                  <span>{availLabel}</span>
                </div>
              )}

              {/* Tooltip z dostepnoscia */}
              {tooltipVisible && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    marginBottom: '4px',
                    zIndex: 100,
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    padding: '0.5rem 0.6rem',
                    fontSize: '0.7rem',
                    color: 'var(--text)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    minWidth: '180px',
                    maxWidth: '260px',
                    whiteSpace: 'normal',
                    pointerEvents: 'none'
                  }}
                >
                  {dayAvailability.map((e, i) => {
                    const emoji = AVAILABILITY_EMOJI[e.typ] || '';
                    const label = AVAILABILITY_LABEL[e.typ] || e.typ || '';
                    const hasHours = e.godzina_od && e.godzina_do;
                    return (
                      <div
                        key={e.id || `${e.data}-${e.typ}-${i}`}
                        style={{
                          marginBottom: i < dayAvailability.length - 1 ? '0.35rem' : 0,
                          paddingBottom: i < dayAvailability.length - 1 ? '0.35rem' : 0,
                          borderBottom: i < dayAvailability.length - 1 ? '1px dashed var(--border)' : 'none'
                        }}
                      >
                        <div style={{ fontWeight: 600 }}>
                          {emoji} {label}
                          {hasHours ? ` ${e.godzina_od}-${e.godzina_do}` : ' (caly dzien)'}
                        </div>
                        {e.notatka && (
                          <div style={{
                            marginTop: '0.2rem',
                            color: 'var(--text-secondary)',
                            fontStyle: 'italic'
                          }}>
                            "{e.notatka}"
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: isToday ? 'var(--info)' : (isWeekend ? 'var(--text-muted)' : 'var(--text)'),
                marginTop: hasAvailability ? '18px' : 0
              }}>
                <span>{date.getDate()}</span>
                {!readOnly && onEmptyDayClick && dayShifts.length === 0 && (
                  <span
                    title="Dodaj zmiane"
                    onClick={(e) => { e.stopPropagation(); onEmptyDayClick(date); }}
                    style={{
                      cursor: 'pointer', color: 'var(--text-muted)',
                      fontSize: '0.7rem', opacity: 0.6
                    }}
                  >
                    +
                  </span>
                )}
              </div>

              {dayShifts.map(s => {
                const color = getStatusColor(s.status);
                const od = parseShiftTime(s.godzina_od);
                const doG = parseShiftTime(s.godzina_do);
                const locShort = shortLocationCode(s.location_nazwa || s.lokalizacja || s.location_name);
                const isAbsence = ['urlop', 'chorobowe', 'nieobecnosc', 'wolne'].includes(s.status);
                const tooltip = [
                  getStatusLabel(s.status),
                  s.user_login ? `Pracownik: ${s.user_login}` : '',
                  s.location_nazwa ? `Lokalizacja: ${s.location_nazwa}` : '',
                  od && doG ? `${od}-${doG}` : '',
                  s.notatki ? `Notatki: ${s.notatki}` : ''
                ].filter(Boolean).join('\n');

                return (
                  <div
                    key={s.id}
                    title={tooltip}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onShiftClick) onShiftClick(s);
                    }}
                    style={{
                      backgroundColor: `${color}22`,
                      borderLeft: `3px solid ${color}`,
                      color: 'var(--text)',
                      borderRadius: '3px',
                      padding: '0.2rem 0.4rem',
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 500
                    }}
                  >
                    {isAbsence ? (
                      <span style={{ color, fontWeight: 600, textTransform: 'uppercase' }}>
                        {getStatusLabel(s.status)}
                      </span>
                    ) : (
                      <span>
                        {od}-{doG}{locShort ? ` ${locShort}` : ''}
                      </span>
                    )}
                  </div>
                );
              })}

              {/* Wspolpracownicy - kto jeszcze jest wrzucony tego dnia */}
              {dayCoworkers.length > 0 && (
                <div style={{
                  marginTop: 'auto',
                  paddingTop: '0.25rem',
                  borderTop: '1px dashed var(--border)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1px'
                }}>
                  {dayCoworkers.map(c => {
                    const name = c.user_login || c.login;
                    const od = parseShiftTime(c.godzina_od);
                    const doG = parseShiftTime(c.godzina_do);
                    const isAbsence = ['urlop', 'chorobowe', 'nieobecnosc', 'wolne'].includes(c.status);
                    const tooltip = [
                      name,
                      isAbsence ? getStatusLabel(c.status) : (od && doG ? `${od}-${doG}` : ''),
                      c.location_nazwa ? `Lokalizacja: ${c.location_nazwa}` : ''
                    ].filter(Boolean).join('\n');
                    return (
                      <div
                        key={`cw-${c.id}`}
                        title={tooltip}
                        style={{
                          fontSize: '0.65rem',
                          color: 'var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        <i className="fas fa-user" style={{ fontSize: '0.55rem', flexShrink: 0, opacity: 0.7 }} />
                        <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                        {isAbsence
                          ? <span style={{ textTransform: 'uppercase' }}>{getStatusLabel(c.status)}</span>
                          : (od && doG && <span style={{ flexShrink: 0 }}>{od}-{doG}</span>)}
                      </div>
                    );
                  })}
                </div>
              )}

              {renderCellExtra && renderCellExtra(date, dayShifts)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthCalendar;
